/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { callApi } from 'api/helpers/api.helpers';
import draftApi from '../../api/draft.api';
import { lookupI18n } from '../utils/objectUtils';
import { populateUISchemaWithRulesResults, processWorkflowValidations } from './businessRules.helper';

/**
 * Function that handles business rules by retrieving and translating workflow errors and uischema errors
 *
 * !!! IMPORTANT A.F. !!!
 * WE HAVE TO WRAP 'handleBusinessRuleValidations' FUNCTION CALLS IN A TRY-CATCH
 * BECAUSE WE CONFIGURED 'draftApi.validateBusinessRules' TO BE CANCELABLE, MEANING IT WILL THROW A CANCEL ERROR
 * CHECK USAGES ON HOW THIS IS DONE
 *
 * @param submission - submission
 * @param defaultUiSchema - current uiSchema
 * @param popupId - popup id (used mainly for dialogs)
 * @param businessRulesPrefix - business rules prefix (used mainly for dialogs)
 * @returns {Promise<{workflowValidations: {isValid: *, extraErrors: {}, sectionValidations: {[p: string]: {percentage: *, messages: *}, [p: number]: {percentage: *, messages: *}}}, uiSchema: *}>}
 */
export const handleBusinessRuleValidations = async (
  submission,
  defaultUiSchema,
  popupId,
  businessRulesPrefix,
  controller,
) => {
  const { sectionValidations, uiSchema } = await callApi(draftApi.validateBusinessRules, {
    abortId: 'submission-validate',
  })(submission, { popupId, businessRulesPrefix }, { signal: controller?.signal });

  const workflowValidations = processWorkflowValidations(sectionValidations);

  const translatedUiSchema = lookupI18n(uiSchema);
  const uiSchemaTransformed = populateUISchemaWithRulesResults(defaultUiSchema, translatedUiSchema);

  return { workflowValidations, uiSchema: uiSchemaTransformed };
};

/**
 * Runs the postProcess rules over the submission object based on a form event.
 * @param {*} submission submission after the event
 * @param {*} event can represent a field change (then it will contain the full field path followed by "_change")
 *                  can represent a section change (then it will contain the name of the section followed by the _operation)
 *                  ....
 * @param {*} id for field event, the full field path
 *               for section event, the id of the section item that triggered the event
 * @returns recomputed submission based on the event (via Camunda postprocess rules)
 */
export const postProcess = async (submission, event, id, controller) => {
  const newSubmission = await callApi(draftApi.postProcessBusinessRules)(submission, event, id, {
    signal: controller?.signal,
  });
  return newSubmission;
};
